/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  CircularProgress,
  FormControl,
  Radio,
  Typography,
} from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';

import { RegisterStage, StatusCode } from '../../../api/enumerations';
import { twoFaEnable, twoFaSend } from '../../../api/register';
import { IconTimerMS } from '../../../constants/icons';
import useErrorMessage from '../../../hooks/useErrorMessage';
import useGeneral from '../../../hooks/useGeneral';
import useSnackbar from '../../../hooks/useSnackbar';
import { MFA } from './MFA';
import OTPInput from './OTPInput';
import {
  ButtonBox,
  CodeButtonBox,
  Counter,
  FilledButton,
  NewCodeButton,
  OutlinedStyledButton,
  RadioBox,
  SectionDialog,
  Span,
  StyledFormControlLabel,
  StyledFormLabel,
  StyledRadioGroup,
  Title,
} from './styles';

interface AuthMethodProps {
  token: string;
  handleNextStage: (stage: RegisterStage) => void;
}

export function AuthMethod({
  token,
  handleNextStage,
}: AuthMethodProps): JSX.Element {
  const [authMethod, setAuthMethod] = useState('2FA');
  const [authLabel, setAuthLabel] = useState('Duplo fator de autenticação');
  const [code, setCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [start, setStart] = useState(false);
  const [startMFA, setStartMFA] = useState(false);
  const [loading, setLoading] = useState(false);

  const { navigateHome } = useGeneral();
  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const handleSendCodeOTP = useCallback(async () => {
    setCode('');
    setTimeLeft(60);

    try {
      const { detail } = await twoFaSend({ token });

      if (detail.description) throw new Error(detail.description);

      if (detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente!');
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, [handleSnackbar, token]);

  const handleEnableCodeOTP = useCallback(async () => {
    setLoading(true);
    try {
      const { detail } = await twoFaEnable({ code, token });

      if (detail.description) throw new Error(detail.description);

      if (detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente!');

      localStorage.setItem(
        'security-auth',
        JSON.stringify({ isEnable: true, authFactor: '1' })
      );
      handleNextStage(RegisterStage.CONFIRMATION_AUTH_METHOD);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  }, [code, handleNextStage, handleSnackbar, token]);

  const handleSetStart = (): void => {
    if (authMethod === 'MFA') {
      setStartMFA(!startMFA);
    } else {
      setStart(!start);
      handleSendCodeOTP();
    }
  };

  const handleChangeRadio = (event: ChangeEvent<HTMLInputElement>): void => {
    setAuthMethod((event.target as HTMLInputElement).value);

    if (event.target.value === 'MFA') {
      setAuthLabel('Múltiplo fator de autenticação');
    } else {
      setAuthLabel('Duplo fator de autenticação');
    }
  };

  useEffect(() => {
    if (start) {
      if (timeLeft <= 0) {
        setTimeLeft(0);
        return undefined;
      }

      const interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [timeLeft, start]);

  useEffect(() => {
    setTimeLeft(60);
    setStart(false);
  }, [authMethod]);

  return (
    <>
      <Title sx={{ mb: '40px' }}>{authLabel}</Title>
      {authMethod === '2FA' ? (
        <>
          <SectionDialog sx={{ maxWidth: '75%', mb: '32px' }}>
            Caso selecione Duplo fator de autenticação (Whatsapp e e-mail),
            enviaremos o código de confirmação para os contatos vinculados à sua
            conta
          </SectionDialog>
          {start && (
            <>
              <OTPInput
                length={6}
                value={code}
                onChange={setCode}
                disabled={!start}
              />
              <Span>Insira o código de 6 dígitos</Span>
            </>
          )}
          {start &&
            (timeLeft !== 0 ? (
              <>
                <Typography fontSize="14px" mt="24px">
                  Um novo código poderá ser solicitado em:
                </Typography>
                <Counter>
                  {IconTimerMS} {timeLeft} segundos
                </Counter>
              </>
            ) : (
              <NewCodeButton
                type="button"
                disabled={!start}
                onClick={handleSendCodeOTP}
              >
                solicitar novo código
              </NewCodeButton>
            ))}
          {start && (
            <CodeButtonBox>
              <OutlinedStyledButton onClick={() => setStart(!start)}>
                cancelar
              </OutlinedStyledButton>
              <FilledButton
                onClick={handleEnableCodeOTP}
                disabled={code.length < 6}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#FFF' }} />
                ) : (
                  'validar'
                )}
              </FilledButton>
            </CodeButtonBox>
          )}
        </>
      ) : (
        <MFA
          start={startMFA}
          setStart={setStartMFA}
          handleNextStage={handleNextStage}
          token={token}
        />
      )}
      {!startMFA && !start && (
        <Box marginTop="40px" textAlign="center">
          <Typography fontSize="20px" fontWeight="600" mb={2}>
            Como você quer proteger seu login?
          </Typography>
          <FormControl>
            <StyledRadioGroup
              onChange={handleChangeRadio}
              defaultValue={authMethod}
            >
              <RadioBox selected={authMethod === '2FA'} defaultChecked>
                <StyledFormControlLabel
                  value="2FA"
                  control={<Radio />}
                  label="Duplo fator de autenticação"
                />
                <StyledFormLabel>
                  Receba o código por SMS ou email
                </StyledFormLabel>
              </RadioBox>
              <RadioBox selected={authMethod === 'MFA'}>
                <StyledFormControlLabel
                  value="MFA"
                  control={<Radio />}
                  label="Múltiplo fator de autenticação"
                />
                <StyledFormLabel>
                  Aplicativo de autenticação (ex: Google Auth)
                </StyledFormLabel>
              </RadioBox>
            </StyledRadioGroup>
          </FormControl>
        </Box>
      )}
      {!start && !startMFA && (
        <ButtonBox>
          <OutlinedStyledButton onClick={navigateHome}>
            pular etapa
          </OutlinedStyledButton>
          <FilledButton onClick={handleSetStart}>começar</FilledButton>
        </ButtonBox>
      )}
    </>
  );
}
